export const APPLY_SECTION_ID = "aplicar";
export const HOW_SECTION_ID = "como-funciona";

/** Top bar height (64px) + 24px breathing room. */
export const HEADER_OFFSET = 88;
export const SCROLL_DURATION_MS = 400;
/** Beyond this distance we jump instantly instead of animating the scroll. */
export const JUMP_THRESHOLD_PX = 1500;
export const APPLY_ARRIVED_EVENT = "svip:apply-arrived";
/** Fired right before an instant jump so scroll-reveals show their content immediately. */
export const PROGRAMMATIC_JUMP_EVENT = "svip:programmatic-jump";

export type ApplyArrivedDetail = { jumped: boolean };

let rafId: number | null = null;

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function jumpTo(targetY: number): void {
  if (rafId !== null) cancelAnimationFrame(rafId);
  rafId = null;
  window.dispatchEvent(new Event(PROGRAMMATIC_JUMP_EVENT));
  window.scrollTo({ top: targetY, left: 0, behavior: "instant" as ScrollBehavior });
}

/** Returns true if it jumped instantly, false if it animated. */
function scrollToY(targetY: number, onDone?: (jumped: boolean) => void): void {
  if (rafId !== null) cancelAnimationFrame(rafId);
  const startY = window.scrollY;
  const distance = targetY - startY;

  if (Math.abs(distance) > JUMP_THRESHOLD_PX || prefersReducedMotion() || Math.abs(distance) < 2) {
    jumpTo(targetY);
    onDone?.(Math.abs(distance) > JUMP_THRESHOLD_PX);
    return;
  }

  const start = performance.now();
  const step = (now: number) => {
    const t = Math.min(1, (now - start) / SCROLL_DURATION_MS);
    window.scrollTo({
      top: startY + distance * easeInOutCubic(t),
      left: 0,
      behavior: "instant" as ScrollBehavior,
    });
    if (t < 1) {
      rafId = requestAnimationFrame(step);
    } else {
      rafId = null;
      onDone?.(false);
    }
  };
  rafId = requestAnimationFrame(step);
}

function targetYFor(el: HTMLElement, extraOffset = 0): number {
  return Math.max(0, el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET - extraOffset);
}

export function scrollToSection(id: string): void {
  const el = document.getElementById(id);
  if (!el) return;
  scrollToY(targetYFor(el), (jumped) => {
    if (id === APPLY_SECTION_ID) {
      window.dispatchEvent(
        new CustomEvent<ApplyArrivedDetail>(APPLY_ARRIVED_EVENT, { detail: { jumped } })
      );
    }
  });
}

export function scrollToElement(el: HTMLElement, extraOffset = 0): void {
  scrollToY(targetYFor(el, extraOffset));
}

export function scrollToApply(): void {
  scrollToSection(APPLY_SECTION_ID);
}

export function scrollToHow(): void {
  scrollToSection(HOW_SECTION_ID);
}
